import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Loading from './Loading';

// Standardized type for upload progress tracking
// Dva odvojena progress bara:
// - uploadProgress: Frontend kontrolira (chunk upload) 0-100%
// - processingProgress: Backend kontrolira (CSV obrada) 0-100%
export interface UploadProgress {
  fileName: string;
  uploadProgress: number;      // 0-100% - Frontend chunk upload
  processingProgress: number;  // 0-100% - Backend processing
  status: 'uploading' | 'processing' | 'completed' | 'error';
  error?: string;
  message?: string;
  step?: string;               // Backend step: chunk_assembly, parsing, preprocessing, processing, streaming, complete
  eta?: number | string;       // Raw ETA value from backend
  etaFormatted?: string;       // Formatted ETA string from backend (e.g., "2m 30s")
  // File tracking from backend
  currentFile?: number;        // Current file being processed (1-based)
  totalFiles?: number;         // Total number of files
  // Processing step details from backend
  currentStep?: number;        // Current processing step
  totalSteps?: number;         // Total processing steps
}

interface ProgressTrackerProps {
  progress: UploadProgress | null;
  className?: string;
  compact?: boolean;
}

/**
 * Component for standardized upload progress display
 * Uses Loading component for progress bar display
 *
 * Backend sends: { step, progress, status, currentStep, totalSteps, eta, etaFormatted, message }
 */
const ProgressTracker: React.FC<ProgressTrackerProps> = ({
  progress,
  className = '',
  compact = false
}) => {
  const { t } = useTranslation('common');

  // Get status text based on current status and step
  const getStatusText = useCallback((): string => {
    // Use message from backend if available (more descriptive)
    // Messages are already translated by the ChunkedUploader via translateMessage()
    // Just display them directly - no need to re-translate
    if (progress?.message) {
      return progress.message;
    }

    if (progress?.status === 'uploading') {
      return t('loading.dataUploading');
    } else if (progress?.status === 'processing') {
      return t('loading.dataProcessing');
    }
    return '';
  }, [progress?.status, progress?.message, t]);

  // Status colors are now fixed per bar:
  // - Upload bar: always blue (primary)
  // - Processing bar: always amber/orange (warning)

  // Check if we should show step counter
  const showStepCounter = useMemo(() => {
    // Show step counter during processing when we have step info
    const hasStepInfo = progress?.currentStep && progress?.totalSteps;
    const isActiveStatus = progress?.status === 'uploading' || progress?.status === 'processing';
    return hasStepInfo && isActiveStatus;
  }, [progress?.currentStep, progress?.totalSteps, progress?.status]);

  // Check if we should show ETA
  const showEta = useMemo(() => {
    // Show ETA during uploading or processing when we have ETA info
    const hasEta = progress?.etaFormatted || progress?.eta;
    const isActiveStatus = progress?.status === 'uploading' || progress?.status === 'processing';
    return hasEta && isActiveStatus;
  }, [progress?.etaFormatted, progress?.eta, progress?.status]);

  if (!progress) return null;

  return (
    <div className={`w-full ${compact ? 'max-w-sm' : 'max-w-lg'} mx-auto my-2 sm:my-4 ${className}`}>
      {/* Status Text - Shows message from backend or default status text */}
      {(progress.status === 'uploading' || progress.status === 'processing') && (
        <div className="mb-1.5 sm:mb-2 text-center">
          <span className="text-xs sm:text-sm font-medium text-foreground">
            {getStatusText()}
          </span>
        </div>
      )}

      {/* File name (when provided) */}
      {progress.fileName && (
        <div className="mb-1 text-center">
          <span className="text-[10px] sm:text-xs font-mono text-muted-foreground truncate inline-block max-w-full">
            {progress.fileName}
          </span>
        </div>
      )}

      {/* Backend step name (sub-line under the headline) */}
      {progress.step && progress.step !== progress.message && (progress.status === 'uploading' || progress.status === 'processing') && (
        <div className="mb-2 text-center">
          <span className="text-[11px] sm:text-xs italic text-muted-foreground">
            {progress.step}
          </span>
        </div>
      )}

      {/* Upload Progress Bar - Blue - Frontend kontrolira */}
      <div className="mb-3 sm:mb-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] sm:text-xs font-medium text-primary">
            {t('loading.uploadProgress') || 'Upload'}
          </span>
          <span className="text-[10px] sm:text-xs font-medium text-primary">
            {Math.round(progress.uploadProgress)}%
          </span>
        </div>
        <Loading
          showProgressBar={true}
          progress={progress.uploadProgress}
          color="primary"
          showPercentage={false}
          minimal={true}
        />
      </div>

      {/* Processing Progress Bar - Orange/Yellow - Backend kontrolira */}
      <div className="mb-1.5 sm:mb-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] sm:text-xs font-medium text-amber-600">
            {t('loading.processingProgress') || 'Processing'}
          </span>
          <span className="text-[10px] sm:text-xs font-medium text-amber-600">
            {Math.round(progress.processingProgress)}%
          </span>
        </div>
        <Loading
          showProgressBar={true}
          progress={progress.processingProgress}
          color="warning"
          showPercentage={false}
          minimal={true}
        />
      </div>

      {/* Step counter - shows which processing step is active */}
      {showStepCounter && (
        <div className="mt-2 sm:mt-3 flex items-center justify-center space-x-1.5 sm:space-x-2 text-xs sm:text-sm">
          <span className="text-primary text-sm sm:text-lg">📊</span>
          <span className="text-muted-foreground">{t('loading.step')}:</span>
          <span className="font-semibold text-foreground">
            {progress.currentStep} / {progress.totalSteps}
          </span>
        </div>
      )}

      {/* File counter - shows which file is being processed */}
      {progress.currentFile && progress.totalFiles && (
        <div className="mt-2 sm:mt-3 flex items-center justify-center space-x-1.5 sm:space-x-2 text-xs sm:text-sm">
          <span className="text-orange-500 text-sm sm:text-lg">📄</span>
          <span className="text-muted-foreground">{t('loading.file')}:</span>
          <span className="font-semibold text-foreground">
            {progress.currentFile} / {progress.totalFiles}
          </span>
        </div>
      )}

      {/* ETA - shows for both uploading and processing */}
      {showEta && (
        <div className="mt-1.5 sm:mt-2 flex items-center justify-center space-x-1.5 sm:space-x-2 text-xs sm:text-sm">
          <span className="text-primary text-sm sm:text-lg">⏱️</span>
          <span className="text-muted-foreground">{t('loading.estimatedTime')}:</span>
          <span className="font-semibold text-primary tabular-nums">
            {progress.etaFormatted || t('loading.calculating')}
          </span>
        </div>
      )}
    </div>
  );
};

export default ProgressTracker;
